import type { CreateMigration, MigrationAnswer, MigrationEvent, MigrationInstance, MigrationJob, MigrationRecoveryAction } from '../types/modpackMigration';

const BASE = 'https://spy.playcdu.co/api/notes/modpack-migrations';

export class ModpackMigrationApiError extends Error {
  constructor(message: string, readonly status: number, readonly code?: string) {
    super(message);
    this.name = 'ModpackMigrationApiError';
  }
}

async function request<T>(path: string, body?: unknown, signal?: AbortSignal, base = BASE): Promise<T> {
  const response = await fetch(`${base}${path}`, {
    method: body === undefined ? 'GET' : 'POST', credentials: 'include', cache: 'no-store', signal,
    ...(body === undefined ? {} : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) throw new ModpackMigrationApiError(
    typeof data?.error === 'string' ? data.error : `The migration service could not complete this request (${response.status}).`,
    response.status, typeof data?.code === 'string' ? data.code : undefined,
  );
  return data as T;
}

/** Writes are never retried automatically: a lost response must be reconciled from the durable job. */
export const modpackMigrationApi = {
  instance: (address: string, signal?: AbortSignal) => request<MigrationInstance>(`/instances/${encodeURIComponent(address)}`, undefined, signal),
  create: (body: CreateMigration) => request<{ job: MigrationJob }>('/jobs', body),
  job: (id: string, signal?: AbortSignal) => request<{ job: MigrationJob }>(`/jobs/${encodeURIComponent(id)}`, undefined, signal),
  events: (id: string, after = 0, signal?: AbortSignal) => request<{ events: MigrationEvent[]; last_sequence: number }>(`/jobs/${encodeURIComponent(id)}/events?after=${after}`, undefined, signal),
  answer: (id: string, requestId: string, value: MigrationAnswer) => request<{ job: MigrationJob; accepted: true }>(`/jobs/${encodeURIComponent(id)}/input`, { request_id: requestId, value }),
  cancel: (id: string) => request<{ job: MigrationJob }>(`/jobs/${encodeURIComponent(id)}/cancel`, {}),
  recover: (id: string, action: MigrationRecoveryAction) => request<{ job: MigrationJob }>(`/jobs/${encodeURIComponent(id)}/recover`, { action }),
};

const APPROVAL_BASE = 'https://spy.playcdu.co/api/notes/staff/manage/migrations';
/** Staff approval has its own server-enforced permission, separate from pack-manager execution. */
export const migrationApprovalApi = {
  list: (signal?: AbortSignal) => request<{ jobs: MigrationJob[] }>('', undefined, signal, APPROVAL_BASE),
  approve: (id: string, requestDigest: string, comment?: string) => request<{ job: MigrationJob }>(`/${encodeURIComponent(id)}/approve`,
    { request_digest: requestDigest, ...(comment?.trim() ? { comment: comment.trim() } : {}) }, undefined, APPROVAL_BASE),
  reject: (id: string, requestDigest: string, reason: string) => request<{ job: MigrationJob }>(`/${encodeURIComponent(id)}/reject`,
    { request_digest: requestDigest, reason: reason.trim() }, undefined, APPROVAL_BASE),
};
